import React, { useState, useEffect } from "react";
import isEmpty from "lodash/isEmpty";
import { connect, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import classNames from "classnames";

import { actions as resultsActions } from "../Results/_ducks";

import { selectResultsError } from "../Results/_ducks/selectors";

import { useStateSelector } from "utils/field";
import { getOptions } from "utils/fields";
import { categories } from "utils/enclosure";
import { getF280SummaryData } from "utils/results/api";
import { getBaseRunId } from "utils/results/runIdentity";
import { getSocApproximationLabel, resolveStoredCaseKey } from "utils/referenceHouse";

import Select from "components/Input/Select";
import Button from "components/Button";
import ExportF280PDF from "./ExportF280PDF";
import LoadingDots from "components/LoadingDots";
import SummaryTable from "./SummaryTable";
import { Stat } from "../Results/General";

import classes from "./style.module.scss";
import resultsClasses from "../Results/style.module.scss";
import sharedClasses from "features/Model/sharedStyles.module.scss";

import WallIcon from "assets/images/components/Wall.svg";
import ExpFloorIcon from "assets/images/components/ExpFloor.svg";
import CeilingFlatIcon from "assets/images/components/Ceiling.svg";
import CeilingAtticIcon from "assets/images/components/CeilingAttic.svg";
import BasementIcon from "assets/images/components/Foundation-Basement.svg";
import CrawlspaceIcon from "assets/images/components/Foundation-Crawlspace.svg";
import SlabIcon from "assets/images/components/Foundation-Slab.svg";
import DoorIcon from "assets/images/components/DoorBG.svg";
import WindowIcon from "assets/images/components/WindowBG.svg";
import FloorHeaderIcon from "assets/images/components/FloorHeader.svg";
import Export from "assets/images/icons/JSX/Export";
import WarningRedIcon from "assets/images/icons/warning-red.svg";
import WarningOrangeIcon from "assets/images/icons/warning-orange.svg";
import ComponentHeading from "features/Model/Enclosure/ComponentList/ComponentHeading";
import Checkbox from "components/Input/Checkbox";
import mixpanel from "mixpanel-browser";
import { getF280DesignHeatingTemp } from "utils/weather";

const { getResults } = resultsActions;

const summaryOrder = {
    basement: {
        title: (
            <div className={classes.componentHeading}>
                <img src={BasementIcon} alt="Wall icon" />
                {""}
                <p>{" Basement"}</p>
            </div>
        ),
    },
    ponyWall: {
        title: (
            <div className={classes.componentHeading}>
                <img src={BasementIcon} alt="Wall icon" />
                {""}
                <p>{" Basement Pony Wall"}</p>
            </div>
        ),
    },
    crawlspaceWall: {
        title: (
            <div className={classes.componentHeading}>
                <img src={CrawlspaceIcon} alt="Wall icon" />
                {""}
                <p>{" Crawl Space Wall (Crawl space included in heated volume)"}</p>
            </div>
        ),
    },
    crawlspaceFloorsAbove: {
        title: (
            <div className={classes.componentHeading}>
                <img src={CrawlspaceIcon} alt="Wall icon" />
                {""}
                <p>{" Floors Above Crawl Space (Crawl space excluded from heated volume)"}</p>
            </div>
        ),
    },
    slab: {
        title: (
            <div className={classes.componentHeading}>
                <img src={SlabIcon} alt="Wall icon" />
                {""}
                <p>{" Slab-on-grade"}</p>
            </div>
        ),
    },

    wall: {
        title: (
            <div className={classes.componentHeading}>
                <img src={WallIcon} alt="Wall icon" />
                {""}
                <p>{" Walls"}</p>
            </div>
        ),
    },
    ceiling: {
        title: (
            <div className={classes.componentHeading}>
                <img src={CeilingAtticIcon} alt="Wall icon" />
                {""}
                <p>{" Ceilings"}</p>
            </div>
        ),
    },
    expFloor: {
        title: (
            <div className={classes.componentHeading}>
                <img src={ExpFloorIcon} alt="Wall icon" />
                {""}
                <p>{" Exposed Floors"}</p>
            </div>
        ),
    },

    floorHeader: {
        title: (
            <div className={classes.componentHeading}>
                <img src={FloorHeaderIcon} alt="Wall icon" />
                {""}
                <p>{" Floor Headers"}</p>
            </div>
        ),
    },
    window: {
        title: (
            <div className={classes.componentHeading}>
                <img src={WindowIcon} alt="Wall icon" />
                {""}
                <p>{" Windows"}</p>
            </div>
        ),
    },
    door: {
        title: (
            <div className={classes.componentHeading}>
                <img src={DoorIcon} alt="Wall icon" />
                {""}
                <p>{" Doors"}</p>
            </div>
        ),
    },
};

const filterByStorey = (summaryComponents, category) => {
    return Object.keys(summaryComponents).reduce((cache, key) => {
        if (
            summaryComponents[key].category === category ||
            (!summaryComponents[key].category && category === "other")
        ) {
            return {
                ...cache,
                [key]: summaryComponents[key],
            };
        }
        return cache;
    }, {});
};

const ComponentSummaries = ({ storeyCategories, summaryComponents, displayUnits, includeLatent }) => {
    return (
        <div>
            {storeyCategories.map((storeyCat, ind) => {
                const componentsInCategory = Object.keys(summaryOrder).map((compType, ind2) => {
                    const filteredComps = filterByStorey(summaryComponents[compType], storeyCat.id);
                    if (!isEmpty(filteredComps)) {
                        return (
                            <div style={{ marginBottom: "0.875rem" }} key={`${ind}-${ind2}`}>
                                {summaryOrder[compType]?.title || <></>}
                                <SummaryTable
                                    compSummary={filteredComps}
                                    compType={compType}
                                    displayUnits={displayUnits}
                                    includeLatent={includeLatent}
                                />
                            </div>
                        );
                    }
                });

                const emptyCategory = componentsInCategory.every((el) => !el);

                return (
                    <div key={ind}>
                        <h4 style={{ marginBottom: "1rem" }}>{storeyCat.name}</h4>
                        {emptyCategory ? (
                            <div className={classes.noComponentsContainer}>
                                <span>No components to display in this category.</span>
                            </div>
                        ) : (
                            componentsInCategory
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const WholeHomeResults = ({
    summaryComponents = {},
    storeyCategories = [],
    displayUnits = "Metric",
    otherMajorContributions,
    nonZeroDuctPipeLosses,
    otherMinorContributions,
    includeLatent = true,
}) => {
    return (
        <>
            {!isEmpty(summaryComponents) && !isEmpty(storeyCategories) && (
                <ComponentSummaries
                    storeyCategories={storeyCategories}
                    summaryComponents={summaryComponents}
                    displayUnits={displayUnits}
                    classes={classes}
                    includeLatent={includeLatent}
                />
            )}
            {!isEmpty(otherMajorContributions) && (
                <div style={{ marginBottom: "0.875rem" }}>
                    <h4
                        style={{
                            marginTop: "1.5rem",
                            marginBottom: "0.875rem",
                        }}
                    >
                        Other Contributions to Heat Loss and Gain
                    </h4>
                    <SummaryTable
                        compSummary={otherMajorContributions}
                        compType="otherMajor"
                        displayUnits={displayUnits}
                        includeLatent={includeLatent}
                    />
                </div>
            )}

            {nonZeroDuctPipeLosses > 0 && (
                <div style={{ marginBottom: "0.875rem" }}>
                    <h4
                        style={{
                            marginTop: "1.5rem",
                            marginBottom: "0.875rem",
                        }}
                    >
                        Distribution System Losses
                    </h4>
                    <SummaryTable
                        compSummary={otherMinorContributions}
                        compType="otherMinor"
                        displayUnits={displayUnits}
                        includeLatent={includeLatent}
                    />
                </div>
            )}
        </>
    );
};

const ROOM_BY_ROOM_ERROR_META = {
    NO_ROOM_SUMMARY: { title: "No rooms found", severity: "error" },
    UNASSIGNED_COMPONENTS: { title: "Unassigned components", severity: "error" },
    INTERNAL_GAINS_SUM: { title: "Internal gains don't sum to 100%", severity: "error" },
    ZERO_ROOM_AREA: { title: "Zero room area", severity: "error" },
    ZERO_EXPOSED_PERIMETER: { title: "Zero exposed perimeter", severity: "error" },
    ORPHANED_COMPONENTS: { title: "Orphaned components", severity: "warning" },
    INVALID_COMPONENT_VALUES: { title: "Invalid component values", severity: "warning" },
    ZERO_LEVEL_HEAT_LOSS: { title: "Zero level heat loss", severity: "warning" },
    ZERO_BUILDING_HEAT_GAIN: { title: "Zero building heat gain", severity: "warning" },
    INVALID_ROOM_RESULT: { title: "Invalid room result", severity: "warning" },
    CALCULATION_EXCEPTION: { title: "Calculation failed", severity: "error" },
};

const formatRoomLevel = (roomLevel) => {
    if (!roomLevel) return null;
    return roomLevel.charAt(0).toUpperCase() + roomLevel.slice(1);
};

export const RoomByRoomErrorSummary = ({ roomByRoomErrors, isLegacyScan = false }) => {
    if (!isLegacyScan && isEmpty(roomByRoomErrors)) {
        return null;
    }

    return (
        <div className={classes.roomByRoomErrorSummary}>
            <h4>Room-by-room Issues</h4>
            {isLegacyScan ? (
                <div className={classNames(classes.roomByRoomErrorCard, classes.roomByRoomErrorCardWarning)}>
                    <div className={classes.roomByRoomErrorCardHeader}>
                        <img src={WarningOrangeIcon} alt="" />
                        <h5>Error scan unavailable</h5>
                    </div>
                    <p>
                        Re-run your simulation to scan for room-by-room errors. Older results do not include a
                        room-by-room error report.
                    </p>
                </div>
            ) : (
                <ul className={classes.roomByRoomErrorList}>
                    {roomByRoomErrors.map((error, index) => {
                        const { code, message, components = [], detail, roomLevel } = error || {};
                        const { title = code || "Issue", severity = "error" } = ROOM_BY_ROOM_ERROR_META[code] || {};
                        const isWarning = severity === "warning";
                        const uniqueComponents = [...new Set(components)].filter(Boolean);
                        const levelLabel = formatRoomLevel(roomLevel);

                        return (
                            <li
                                key={`${code || "error"}-${index}`}
                                className={classNames(classes.roomByRoomErrorCard, {
                                    [classes.roomByRoomErrorCardWarning]: isWarning,
                                    [classes.roomByRoomErrorCardError]: !isWarning,
                                })}
                            >
                                <div className={classes.roomByRoomErrorCardHeader}>
                                    <img src={isWarning ? WarningOrangeIcon : WarningRedIcon} alt="" />
                                    <h5>{title}</h5>
                                    {levelLabel && <span className={classes.roomByRoomErrorBadge}>{levelLabel}</span>}
                                </div>
                                <p>{message}</p>
                                {detail && <p className={classes.roomByRoomErrorDetail}>{detail}</p>}
                                {!isEmpty(uniqueComponents) && (
                                    <div className={classes.roomByRoomErrorChips}>
                                        {uniqueComponents.map((componentLabel) => (
                                            <span key={componentLabel} className={classes.roomByRoomErrorChip}>
                                                {componentLabel}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

const F280Summary = React.memo(({ getResults, modelSave, upgradeExportOpts }) => {
    const history = useHistory();
    const resultsError = useSelector(selectResultsError);

    const [exporting, toggleExporting] = useState(false);
    const [upgradeSelected, toggleUpgradeSelected] = useState("");
    const [exportUnits, toggleExportUnits] = useState("");
    const [f280DisplayMode, toggleF280DisplayMode] = useState("whole"); //"whole", "room"
    const [isPdfExportOpen, setIsPdfExportOpen] = useState(false);
    const [exportData, setExportData] = useState(false);
    const [includeLatent, setIncludeLatent] = useState(true);
    const [outdoorTemp, setOutdoorTemp] = useState(-5);
    const [outdoorTempInput, setOutdoorTempInput] = useState("-5");

    const {
        modelId,
        modelDetails: { name, notes, isSimulationRunning = false, lastCliRun: { cliId: clidIds = {} } = {} } = {},
        modelData: {
            location: { weather: { f280: { location: { id: f280LocationId = 0 } = {} } = {} } = {} } = {},
        } = {},
    } = useStateSelector("model") || {};

    const dhdbt = getF280DesignHeatingTemp(f280LocationId);

    const {
        packageResults: packageResultsData = {},
        loading = false,
        error = false,
    } = useStateSelector("results") || {};
    const {
        name: userName = "",
        email = "",
        userMeta: { company = "" } = {},
        userPermissions: { VOLTA_DEV = false } = {},
        userPreferences: {
            companyPhone = "",
            province: { id: provinceId } = {},
            companyAddress = "",
            companyCity = "",
            companyPostalCode = "",
            companyFax = "",
        } = {},
        isUserVerified = true,
    } = useStateSelector("user") || {};

    const cliId = getBaseRunId(clidIds);

    const { packages = {} } = useStateSelector("upgrades") || {};
    const { base: { metadata: { mode, effectiveOperatingConditions = "" } = {} } = {} } = packageResultsData || {};
    const socApproximationLabel = getSocApproximationLabel(effectiveOperatingConditions);

    const modelUnits = useStateSelector("model.modelData.uiSettings.primaryUnits") || {};

    const { id: numStoreysId } = useStateSelector("model.modelData.specifications.numStoreys") || {};

    const storeyCategories = categories(numStoreysId);

    let cityProvinceLabel = "";
    if (companyCity != "") {
        cityProvinceLabel = companyCity;
    }
    if (provinceId != null) {
        const provLabel =
            getOptions({ fieldKey: "weatherRegion", version: "v11-11" }).find((opt) => opt?.value?.id === provinceId)
                ?.label || "";

        cityProvinceLabel = `${cityProvinceLabel}${cityProvinceLabel !== "" ? ", " : ""}${provLabel}`;
    }

    const calcPerformedBy = {
        userName,
        company,
        companyAddress,
        cityProvince: cityProvinceLabel,
        companyPostalCode,
        companyPhone,
        companyFax,
        email,
    };

    const displayUnits = exportUnits || modelUnits || "Metric";

    const selectedResultPackageId = upgradeSelected || "base";
    const selectedResultPackage = packageResultsData?.[selectedResultPackageId] || {};
    const resultsData = selectedResultPackage.resultsData || {};
    const caseKey = resolveStoredCaseKey({
        resultsData,
        packageId: selectedResultPackageId,
        packageType: packages?.[selectedResultPackageId]?.packageType,
        resultsRole: selectedResultPackage.metadata?.resultsRole,
        operatingConditions: packages?.[selectedResultPackageId]?.ui?.operatingConditions,
    });
    const { [caseKey]: { f280Results = {} } = {} } = resultsData;

    const {
        coolingOverrideSetPoint,
        heatLoss,
        heatingOverrideSetPoint,
        nanLabels = [],
        latHeatGain,
        sensHeatGain,
        summaryDetails: {
            components: summaryComponents = {},
            otherMajor: otherMajorContributions = {},
            otherMinor: otherMinorContributions = {},
        } = {},
        roomByRoomResults = {},
        roomByRoomErrors,
    } = f280Results || {};

    // Legacy results predate roomByRoomErrors; an explicit empty array means a new scan found no issues.
    // Ignore the empty default object from a missing caseKey path so we don't falsely show "legacy".
    const hasLoadedF280Results = !isEmpty(f280Results);
    const isLegacyRoomByRoomScan = hasLoadedF280Results && roomByRoomErrors === undefined;
    const resolvedRoomByRoomErrors = roomByRoomErrors || [];

    const hasRxRResults = !isEmpty(roomByRoomResults);
    const hasResults = !!mode;

    const nanComponentMessage = isEmpty(nanLabels)
        ? null
        : `Some components have produced calculation errors. Please check the inputs for the following component(s) and make corrections where required, ensuring no fields are left blank: ${nanLabels.join(
              ", "
          )}`;

    const handleExport = async () => {
        toggleExporting(true);
        //handled in an action and cannot yet be disabled at the source, like handleSubmit
        // if (!readOnly) {
        //     await saveDrawingData();
        // }

        await modelSave();

        const { data: returnData = {} } = await getF280SummaryData({
            modelId,
            upgradePackageId: upgradeSelected,
        });

        setIsPdfExportOpen(true);
        setExportData(returnData);

        toggleExporting(false);
    };

    useEffect(() => {
        if (!hasResults && Object.keys(clidIds).length > 0 && resultsError !== "Results not found.") {
            const runIds = Object.values(clidIds).map(({ runId = "" }) => runId);
            // Get most recent cli results
            getResults({ cliIds: runIds, modelId });
        }
    }, [clidIds, hasResults, modelId]);

    const nonZeroDuctPipeLosses = Object.values(otherMinorContributions).reduce(
        (sum, curr) => sum + curr.heatLoss + curr.heatGain,
        0
    );

    // Calculate interpolated heat loss based on outdoor temperature
    const calculateInterpolatedHeatLoss = (temp) => {
        if (!heatLoss || !dhdbt) return 0;

        // Linear interpolation between (dhdbt, heatLoss) and (16, 0)
        // Heat loss is 0 at 16°C
        if (temp >= 16) return 0;
        // if (temp <= dhdbt) return heatLoss;

        // Linear interpolation formula: y = y1 + (y2 - y1) * (x - x1) / (x2 - x1)
        const interpolatedValue = (heatLoss * (16 - temp)) / (16 - dhdbt);
        return Math.max(0, interpolatedValue);
    };

    const interpolatedHeatLoss = calculateInterpolatedHeatLoss(outdoorTemp);

    return (
        <div className={sharedClasses.section}>
            <div>
                {loading && !isSimulationRunning ? (
                    <LoadingDots className={classes.dots} />
                ) : hasResults ? (
                    <div className={classes.summaryHeader}>
                        <div>
                            <h4>CSA F280-12 Calculation Summary</h4>
                            <p>View the details of the heat loss and heat gain summary.</p>
                            {socApproximationLabel && <p>{socApproximationLabel}</p>}
                        </div>
                    </div>
                ) : (
                    <div className={resultsClasses.intro}>
                        <p>
                            No results to display. Once ready, click Run New Simulation to see your model's performance
                            (v11.11 onward).
                        </p>
                    </div>
                )}
            </div>
            {isSimulationRunning && (
                <div className={resultsClasses.simulationRunningNotice}>
                    <p>
                        <LoadingDots /> A new simulation is in progress. The results should appear momentarily.
                    </p>
                </div>
            )}
            {cliId && hasResults && !error && (
                <>
                    {(coolingOverrideSetPoint != null || heatingOverrideSetPoint != null) && (
                        <div className={classNames(classes.noticeContainer, classes.setPointNotice)}>
                            <p>
                                The results below have been generated using indoor set points that do not align with CSA
                                F280-12. Visit the Mechanicals / Temperatures tab to revert the set points used for
                                calculations. (Heating set point used: {heatingOverrideSetPoint || "22"}˚C. Cooling set
                                point used: {coolingOverrideSetPoint || "24"}˚C)
                            </p>
                        </div>
                    )}
                    {nanComponentMessage && (
                        <div className={classNames(classes.noticeContainer, classes.setPointNotice)}>
                            <p>{nanComponentMessage}</p>
                        </div>
                    )}
                    <div className={classes.export}>
                        <Select
                            className={classes.selectUpgrade}
                            name="exportIncluded"
                            options={[
                                {
                                    value: "",
                                    label: "None",
                                },
                                ...upgradeExportOpts,
                            ]}
                            label="Upgrade Package" // Included in Export"
                            placeholder="None"
                            input={{
                                value: upgradeSelected,
                                onChange: (value) => toggleUpgradeSelected(value),
                            }}
                            large
                        />
                        <Select
                            name="exportUnits"
                            options={[
                                {
                                    value: "",
                                    label: "Same as Model",
                                },
                                {
                                    value: "Metric",
                                    label: "Metric (W, RSI)",
                                },
                                {
                                    value: "Imperial",
                                    label: "Imperial (Btu/h, R)",
                                },
                            ]}
                            label="Units" // for Export"
                            input={{
                                value: exportUnits,
                                onChange: (value) => toggleExportUnits(value),
                            }}
                            large
                        />

                        <Select
                            name="f280DisplayMode"
                            options={[
                                {
                                    value: "whole",
                                    label: "Whole-building",
                                },
                                {
                                    value: "room",
                                    label: "Room-by-room",
                                },
                            ]}
                            label="Calculation Mode"
                            input={{
                                value: f280DisplayMode,
                                onChange: (value) => {
                                    mixpanel.track("F280 Results Type Toggled", {
                                        ResultsType: value,
                                    });
                                    toggleF280DisplayMode(value);
                                },
                            }}
                            large
                        />

                        <Checkbox
                            name="includeLatent"
                            label="Include Latent Multiplier"
                            input={{
                                onChange: () => setIncludeLatent((prevState) => !prevState),
                                value: includeLatent,
                            }}
                            large
                        />

                        <Button
                            smallPadding
                            onClick={handleExport}
                            disabled={!isUserVerified || exporting || !isEmpty(nanLabels)}
                            icon={Export}
                            className={classes.exportButton}
                        >
                            {exporting ? "Downloading..." : "Download F280 Report"}
                        </Button>
                    </div>
                    <h4
                        style={{
                            marginTop: "1.875rem",
                            marginBottom: "1rem",
                        }}
                    >
                        Heat Loss/Gain Summary
                    </h4>
                    <div
                        className={`${resultsClasses.row} ${resultsClasses.totals}`}
                        style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr" }}
                    >
                        <Stat
                            label="Design Heat Loss"
                            value={(displayUnits === "Metric" ? heatLoss : heatLoss * 3.41214) || "N/A"}
                            unit={displayUnits === "Metric" ? "W" : "Btu/h"}
                            decimals={0}
                            big
                        />
                        {dhdbt != null && (
                            <div
                                className={resultsClasses.stat}
                                style={{
                                    backgroundColor: "#f8f9fa",
                                    padding: "0.625rem 1rem 1rem 1rem",
                                    borderRadius: "0.5rem",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.5rem",
                                        marginBottom: "0.5rem",
                                    }}
                                >
                                    <p style={{ fontSize: "0.75rem", marginBottom: "0", fontWeight: "700" }}>
                                        Heat Loss at Outdoor Temp (°C):
                                    </p>
                                    <input
                                        type="number"
                                        value={outdoorTempInput}
                                        onChange={(e) => {
                                            const inputValue = e.target.value;
                                            // Allow user to type freely (including "-", "-1", etc.)
                                            setOutdoorTempInput(inputValue);
                                            // Update numeric state if we have a valid number
                                            const parsed = parseFloat(inputValue);
                                            if (!isNaN(parsed) && inputValue !== "" && inputValue !== "-") {
                                                setOutdoorTemp(parsed);
                                            }
                                        }}
                                        onBlur={(e) => {
                                            // Clamp and validate the value when user leaves the field
                                            const parsed = parseFloat(e.target.value);
                                            const min = Math.min(dhdbt, -22) || -40;
                                            if (!isNaN(parsed)) {
                                                const clamped = Math.min(Math.max(parsed, min), 16);
                                                setOutdoorTemp(clamped);
                                                setOutdoorTempInput(clamped.toString());
                                            } else {
                                                // If invalid, reset to current valid value
                                                const clamped = Math.min(Math.max(outdoorTemp, min), 16);
                                                setOutdoorTemp(clamped);
                                                setOutdoorTempInput(clamped.toString());
                                            }
                                        }}
                                        style={{
                                            width: "50px",
                                            padding: "0.125rem 0.25rem",
                                            border: "1px solid #d1d5db",
                                            borderRadius: "0.25rem",
                                            fontSize: "0.75rem",
                                            backgroundColor: "#ffffff",
                                            textAlign: "center",
                                        }}
                                        min={Math.min(dhdbt, -22) || -40}
                                        max={16}
                                        step={1}
                                    />
                                </div>
                                <span style={{ fontSize: "1.25rem" }}>
                                    <strong style={{ fontWeight: "700", fontSize: "1.5rem" }}>
                                        {typeof (displayUnits === "Metric"
                                            ? interpolatedHeatLoss
                                            : interpolatedHeatLoss * 3.41214) === "number"
                                            ? (displayUnits === "Metric"
                                                  ? interpolatedHeatLoss
                                                  : interpolatedHeatLoss * 3.41214
                                              )
                                                  .toFixed(0)
                                                  .replace("-0.00", "0.00")
                                            : (displayUnits === "Metric"
                                                  ? interpolatedHeatLoss
                                                  : interpolatedHeatLoss * 3.41214) || "N/A"}
                                    </strong>{" "}
                                    {displayUnits === "Metric" ? "W" : "Btu/h"}
                                </span>
                            </div>
                        )}

                        <Stat
                            label="Design Heat Gain (Sensible + Latent)"
                            value={(displayUnits === "Metric" ? latHeatGain : latHeatGain * 3.41214) || "N/A"}
                            unit={displayUnits === "Metric" ? "W" : "Btu/h"}
                            decimals={0}
                            big
                        />
                        <Stat
                            label="Design Heat Gain (Sensible)"
                            value={(displayUnits === "Metric" ? sensHeatGain : sensHeatGain * 3.41214) || "N/A"}
                            unit={displayUnits === "Metric" ? "W" : "Btu/h"}
                            decimals={0}
                        />
                    </div>
                    {f280DisplayMode === "whole" && isEmpty(summaryComponents) && (
                        <div className={classNames(classes.noticeContainer, classes.setPointNotice)}>
                            <p>
                                Re-run your simulation to generate a breakdown of heat loss and gain by building
                                component.
                            </p>
                        </div>
                    )}
                    {f280DisplayMode === "room" && !hasRxRResults && (
                        <div className={classNames(classes.noticeContainer, classes.setPointNotice)}>
                            <p>
                                Re-run your simulation to generate a breakdown of heat loss and gain by room. Room View
                                must be enabled in the Enclosure tab to generate Room-by-room results.
                            </p>
                        </div>
                    )}
                    {f280DisplayMode === "room" && (
                        <div
                            className={classNames(classes.noticeContainer, classes.roomByRoomNotice)}
                            onClick={() => history.push(`/insights/${modelId}`)}
                        >
                            <span>Notice:</span> Room by Room results have been moved to Insights.
                        </div>
                    )}
                    {f280DisplayMode === "room" && (
                        <RoomByRoomErrorSummary
                            roomByRoomErrors={resolvedRoomByRoomErrors}
                            isLegacyScan={isLegacyRoomByRoomScan}
                        />
                    )}
                    {f280DisplayMode === "whole" && (
                        <WholeHomeResults
                            summaryComponents={summaryComponents}
                            storeyCategories={storeyCategories}
                            displayUnits={displayUnits}
                            otherMajorContributions={otherMajorContributions}
                            nonZeroDuctPipeLosses={nonZeroDuctPipeLosses}
                            otherMinorContributions={otherMinorContributions}
                            includeLatent={includeLatent}
                        />
                    )}
                </>
            )}
            <ExportF280PDF
                isPdfExportOpen={isPdfExportOpen}
                handleClose={() => {
                    setIsPdfExportOpen(false);
                    setExportData({});
                }}
                displayData={exportData}
                name={name}
                notes={notes}
                calcPerformedBy={calcPerformedBy}
                heatLossValue={displayUnits === "Metric" ? heatLoss : heatLoss * 3.41214}
                latHeatGainValue={displayUnits === "Metric" ? latHeatGain : latHeatGain * 3.41214}
                latentMultiplier={includeLatent ? 1.3 : 1}
                heatUnit={displayUnits === "Metric" ? "W" : "Btu/h"}
                roomByRoomResults={roomByRoomResults}
                storeyCategories={storeyCategories}
                displayUnits={displayUnits}
                allowedRoomByRoom={true}
            />
        </div>
    );
});

const mapDispatchToProps = (dispatch) => ({
    getResults: ({ cliIds, modelId }) => dispatch(getResults({ cliIds, modelId })),
});

export default connect(null, mapDispatchToProps)(F280Summary);
